// The single place where a view's cards are filtered, searched, sorted and
// grouped. Upstream spreads this over store/cards.ts selectors + boardUtils;
// the clone keeps one pure function the four views share.

import {Board, IPropertyTemplate} from '../types/board'
import {BoardView} from '../types/boardView'
import {Card} from '../types/card'
import {IUser} from '../types/user'
import {Constants} from '../common/constants'
import {CardFilter} from './cardFilter'
import {sortCards} from './sortCards'
import {displayPropertyValue} from './properties'
import {getVisibleAndHiddenGroups} from './boardUtils'

export type ViewCards = {
    visible: Card[]
    groups: ReturnType<typeof getVisibleAndHiddenGroups>['visible']
    hiddenGroups: ReturnType<typeof getVisibleAndHiddenGroups>['hidden']
    hiddenCardsCount: number
    groupByProperty: IPropertyTemplate | undefined
}

function matchesSearch(card: Card, board: Board, users: Record<string, IUser>, needle: string): boolean {
    const haystack = [
        card.title,
        card.fields.icon || '',
        ...board.cardProperties.map((p) => displayPropertyValue(card.fields.properties[p.id], p, users)),
    ].join(' ').toLowerCase()
    return haystack.includes(needle)
}

export function visiblePropertyTemplates(board: Board, view: BoardView): IPropertyTemplate[] {
    const {visiblePropertyIds} = view.fields
    const templates = visiblePropertyIds.length ?
        visiblePropertyIds.map((id) => board.cardProperties.find((p) => p.id === id)).filter(Boolean) :
        board.cardProperties
    return [
        {id: Constants.titleColumnId, name: '标题', type: 'text', options: []},
        ...(templates as IPropertyTemplate[]),
    ]
}

export function computeViewCards(
    board: Board,
    view: BoardView | undefined,
    cards: Card[],
    usersById: Record<string, IUser> = {},
    searchText = '',
): ViewCards {
    if (!view) {
        return {visible: [], groups: [], hiddenGroups: [], hiddenCardsCount: 0, groupByProperty: undefined}
    }

    const filtered = CardFilter.applyFilterGroup(view.fields.filter, board.cardProperties, cards)
    const needle = searchText.trim().toLowerCase()
    const searched = needle ? filtered.filter((card) => matchesSearch(card, board, usersById, needle)) : filtered
    const sorted = sortCards(searched, board, view, usersById)

    const groupByProperty = view.fields.groupById ?
        board.cardProperties.find((p) => p.id === view.fields.groupById) :
        undefined

    if (groupByProperty && (view.fields.viewType === 'board' || view.fields.viewType === 'table' || view.fields.viewType === 'gallery')) {
        const {visible: groups, hidden: hiddenGroups} = getVisibleAndHiddenGroups(
            sorted,
            view.fields.visibleOptionIds,
            view.fields.hiddenOptionIds,
            groupByProperty,
        )
        return {
            visible: sorted,
            groups,
            hiddenGroups,
            hiddenCardsCount: hiddenGroups.reduce((acc, g) => acc + g.cards.length, 0),
            groupByProperty,
        }
    }

    return {
        visible: sorted,
        groups: [],
        hiddenGroups: [],
        hiddenCardsCount: 0,
        groupByProperty,
    }
}

/** Cards ordered by the view's cardOrder, used when appending on drag-drop. */
export function cardOrderOf(view: BoardView, cards: Card[]): Card[] {
    const byId = new Map(cards.map((c) => [c.id, c]))
    const ordered = view.fields.cardOrder.map((id) => byId.get(id)).filter(Boolean) as Card[]
    const rest = cards.filter((c) => !view.fields.cardOrder.includes(c.id))
    return [...ordered, ...rest]
}
